import styled from 'styled-components';

type SourcePage = 'links' | 'store';

const FormCard = styled.form<{ $compact?: boolean; $hasVisual?: boolean; $sourcePage?: SourcePage }>`
  background: ${({ $compact, theme }) => (
    $compact
      ? 'transparent'
      : theme.colors.blue2
  )};
  border: ${({ $compact, theme }) => ($compact ? '0' : `1px solid ${theme.colors.blue3}`)};
  border-radius: ${({ $compact }) => ($compact ? '14px' : '16px')};
  box-shadow: ${({ $compact }) => ($compact ? 'none' : 'inset 0 1px 0 rgba(250, 252, 255, 0.04)')};
  display: ${({ $hasVisual }) => ($hasVisual ? 'grid' : 'block')};
  grid-template-columns: ${({ $hasVisual, $sourcePage }) => {
    if (!$hasVisual) return '1fr';
    return $sourcePage === 'links'
      ? '1fr'
      : 'minmax(0, 1fr) minmax(340px, 0.48fr)';
  }};
  overflow: hidden;

  @media (max-width: 620px) {
    grid-template-columns: 1fr;
  }
`;

const ContentPanel = styled.div<{ $sourcePage?: SourcePage }>`
  display: grid;
  gap: ${({ $sourcePage }) => ($sourcePage === 'links' ? '0.95rem' : '1.35rem')};
  padding: ${({ $sourcePage }) => (
    $sourcePage === 'links'
      ? '1.25rem'
      : '2rem 2rem 1.9rem'
  )};

  @media (max-width: 620px) {
    padding: 1.5rem;
  }
`;

const Copy = styled.div`
  min-width: 0;
`;

const VisualPanel = styled.div<{ $sourcePage?: SourcePage }>`
  min-height: ${({ $sourcePage }) => ($sourcePage === 'links' ? '158px' : '240px')};
  background:
    radial-gradient(circle at 88% 8%, rgba(109, 26, 224, 0.34), transparent 42%),
    ${({ theme }) => theme.colors.blue1};
  border-left: ${({ $sourcePage, theme }) => ($sourcePage === 'links' ? '0' : `1px solid ${theme.colors.blue3}`)};
  border-top: ${({ $sourcePage, theme }) => ($sourcePage === 'links' ? `1px solid ${theme.colors.blue3}` : '0')};
  overflow: hidden;
  position: relative;

  @media (max-width: 620px) {
    border-left: 0;
    border-top: 1px solid ${({ theme }) => theme.colors.blue3};
    min-height: 200px;
  }
`;

const VisualGlow = styled.div`
  position: absolute;
  right: -70px;
  top: -90px;
  width: 280px;
  height: 280px;
  border-radius: 50%;
  background: #6d1ae0;
  filter: blur(8px);
  opacity: 0.16;
`;

const ProductBox = styled.img<{ $sourcePage?: SourcePage }>`
  position: absolute;
  left: 12%;
  top: 50%;
  width: ${({ $sourcePage }) => ($sourcePage === 'links' ? '48%' : '46%')};
  min-width: ${({ $sourcePage }) => ($sourcePage === 'links' ? '132px' : '170px')};
  transform: translateY(-46%);
  filter: drop-shadow(0 18px 32px rgba(0, 0, 0, 0.65));
`;

const GuideCover = styled.img<{ $sourcePage?: SourcePage }>`
  position: absolute;
  right: 12%;
  top: 50%;
  width: ${({ $sourcePage }) => ($sourcePage === 'links' ? '24%' : '22%')};
  min-width: ${({ $sourcePage }) => ($sourcePage === 'links' ? '68px' : '86px')};
  border: 1px solid #3a3d42;
  border-radius: 6px;
  box-shadow: 0 16px 34px rgba(0, 0, 0, 0.6);
  transform: translateY(-62%) rotate(6deg);
`;

const CopyText = styled.div<{ $sourcePage?: SourcePage }>`
  min-width: 0;
  display: grid;
  gap: ${({ $sourcePage }) => ($sourcePage === 'links' ? '0.55rem' : '1rem')};

  h2 {
    color: ${({ theme }) => theme.colors.blue9};
    font-size: ${({ $sourcePage }) => (
      $sourcePage === 'links'
        ? 'clamp(1.1rem, 5vw, 1.3rem)'
        : 'clamp(1.35rem, 2.4vw, 1.65rem)'
    )};
    font-weight: 800;
    line-height: ${({ $sourcePage }) => ($sourcePage === 'links' ? '1.18' : '1.14')};
    margin: 0;
  }

  p {
    color: ${({ theme }) => theme.colors.blue11};
    font-size: ${({ $sourcePage }) => (
      $sourcePage === 'links'
        ? '0.86rem'
        : 'clamp(0.92rem, 1.4vw, 1rem)'
    )};
    line-height: ${({ $sourcePage }) => ($sourcePage === 'links' ? '1.45' : '1.55')};
    margin: 0;
    max-width: 28em;
  }
`;

const Kicker = styled.div`
  color: ${({ theme }) => theme.colors.blue11};
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: uppercase;
`;

const FieldGroup = styled.div<{ $sourcePage?: SourcePage }>`
  display: grid;
  gap: ${({ $sourcePage }) => ($sourcePage === 'links' ? '0.45rem' : '0.65rem')};
`;

const Label = styled.label`
  color: ${({ theme }) => theme.colors.blue9};
  font-size: clamp(0.92rem, 1.5vw, 1.05rem);
  font-weight: 800;
`;

const InputRow = styled.div<{ $sourcePage?: SourcePage }>`
  display: grid;
  grid-template-columns: 1fr auto;
  margin-right: 20%;
  align-items: stretch;
  background: ${({ theme }) => theme.colors.blue1};
  border: 1px solid ${({ theme }) => theme.colors.blue3};
  border-radius: ${({ $sourcePage }) => ($sourcePage === 'links' ? '12px' : '18px')};
  padding: ${({ $sourcePage }) => ($sourcePage === 'links' ? '4px' : '6px')};

  @media (max-width: 420px) {
    grid-template-columns: 1fr auto;
  }
`;

const Input = styled.input<{ $sourcePage?: SourcePage }>`
  min-width: 0;
  width: 100%;
  min-height: ${({ $sourcePage }) => ($sourcePage === 'links' ? '44px' : '52px')};
  background: transparent;
  border: 0;
  border-radius: ${({ $sourcePage }) => ($sourcePage === 'links' ? '9px' : '12px')};
  box-sizing: border-box;
  color: ${({ theme }) => theme.colors.blue9};
  font-family: inherit;
  font-size: ${({ $sourcePage }) => ($sourcePage === 'links' ? '0.92rem' : '1rem')};
  line-height: 1.1;
  padding: ${({ $sourcePage }) => ($sourcePage === 'links' ? '0.75rem 0.85rem' : '0.85rem 1rem')};

  &:focus {
    border-color: ${({ theme }) => theme.colors.blue8};
    outline: none;
  }
`;

const SubmitButton = styled.button<{ $sourcePage?: SourcePage }>`
  min-height: ${({ $sourcePage }) => ($sourcePage === 'links' ? '44px' : '52px')};
  background: ${({ theme }) => theme.colors.blue9};
  border: 1px solid ${({ theme }) => theme.colors.blue9};
  border-radius: ${({ $sourcePage }) => ($sourcePage === 'links' ? '9px' : '12px')};
  box-sizing: border-box;
  color: ${({ theme }) => theme.colors.blue1};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: inherit;
  font-size: ${({ $sourcePage }) => ($sourcePage === 'links' ? '0.9rem' : '1rem')};
  font-weight: 800;
  line-height: 1.1;
  padding: ${({ $sourcePage }) => ($sourcePage === 'links' ? '0 1rem' : '0 1.25rem')};
  transition: background 0.2s ease, border-color 0.2s ease, transform 0.2s ease;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.blue12};
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.7;
    cursor: wait;
  }
`;

const Message = styled.div<{ $status: 'idle' | 'loading' | 'success' | 'error' }>`
  min-height: 1.2em;
  color: ${({ theme, $status }) => ($status === 'error' ? '#ffb4a8' : theme.colors.blue11)};
  font-size: 0.78rem;
  line-height: 1.45;
`;

const Note = styled.p<{ $sourcePage?: SourcePage }>`
  align-items: center;
  color: ${({ theme }) => theme.colors.blue8};
  display: flex;
  font-size: ${({ $sourcePage }) => ($sourcePage === 'links' ? '0.78rem' : '0.82rem')};
  gap: 0.5rem;
  line-height: 1.35;
  margin: -0.15rem 0 0;

  &::before {
    background: ${({ theme }) => theme.colors.blue8};
    border-radius: 50%;
    content: '';
    flex: 0 0 auto;
    height: 5px;
    width: 5px;
  }
`;

export const Styles = {
  FormCard,
  ContentPanel,
  Copy,
  CopyText,
  VisualPanel,
  VisualGlow,
  ProductBox,
  GuideCover,
  Kicker,
  FieldGroup,
  Label,
  InputRow,
  Input,
  SubmitButton,
  Note,
  Message,
};
